import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { History, Sparkles, Users } from 'lucide-react';

import { useBookDetail } from '@/features/library/hooks/useBookDetail';
import { useMySections } from '@/features/chapters/hooks/useMySections';
import { useUserLibraryForTab } from '@/features/library/hooks/useUserLibrary';
import ChapterModal from '../components/ChapterModal';

import LearningNode from '../components/LearningNode';
import type { LearningSession } from '../types';
import TimelineBottomSheet from '@/features/learning/components/TimelineBottomSheet';
import CharacterAnalysisBottomSheet from '@/features/learning/components/CharacterAnalysisBottomSheet';
import NovelCharacterChat from '@/features/reading/components/NovelCharacterChat';
import { useTimelineEvents } from '@/features/learning/hooks/useTimelineEvents';
import { useCharacterAnalysis } from '@/features/learning/hooks/useCharacterAnalysis';
import { useCharacterRelationships } from '@/features/learning/hooks/useCharacterRelationships';
import { useBookCharacters } from '@/features/library/hooks/useBookCharacters';
import HeroPromoBanner from '@/features/library/components/HeroPromoBanner';
import { paymentServiceApi } from '@/services/api';
import { getBookTheme } from '../utils/bookTheme';
import { analyticsService } from '@/services/analyticsService';
import {
  getNovelFeatureSpoil,
  setNovelFeatureSpoil,
} from '@/utils/novelFeatureSpoil';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';
import ReferralInviteModal from '../components/ReferralInviteModal';

interface LocationState {
  bookId?: string | number;
  disableBookSwitcher?: boolean;
  fromOnboarding?: boolean;
  source?: string;
  notificationCampaign?: string;
  showReferralInvite?: boolean;
}

interface OnboardingFeatureFlags {
  hasChatCharacters: boolean;
  hasCharacterInsights: boolean;
  hasTimeline: boolean;
}

const LearningPath: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const isOnboardingRoute = location.pathname.startsWith('/onboarding/');
  const onboardingStoredBookId = isOnboardingRoute
    ? localStorage.getItem('onboarding_book_id')
    : null;
  const bookId = state?.bookId
    ? String(state.bookId)
    : onboardingStoredBookId || null;
  const disableBookSwitcher =
    state?.disableBookSwitcher === true || isOnboardingRoute;
  const fromOnboarding = state?.fromOnboarding === true || isOnboardingRoute;
  const navigate = useNavigate();
  const bookIdNum = bookId ? parseInt(bookId, 10) : 0;
  const bookTheme = getBookTheme(bookIdNum);
  const [selectedSession, setSelectedSession] =
    useState<LearningSession | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isCharacterAnalysisOpen, setIsCharacterAnalysisOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [showReferralInvite, setShowReferralInvite] = useState(() => state?.showReferralInvite === true);
  const [isTimelineSpoil, setIsTimelineSpoil] = useState(() =>
    fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'timeline')
  );
  const [isCharacterAnalysisSpoil, setIsCharacterAnalysisSpoil] = useState(() =>
    fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'character-analysis')
  );
  const [isCharacterRelationshipsSpoil, setIsCharacterRelationshipsSpoil] =
    useState(() =>
      fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'character-relationships')
    );

  useEffect(() => {
    setIsTimelineSpoil(fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'timeline'));
    setIsCharacterAnalysisSpoil(
      fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'character-analysis')
    );
    setIsCharacterRelationshipsSpoil(
      fromOnboarding || getNovelFeatureSpoil(bookIdNum || undefined, 'character-relationships')
    );
  }, [bookIdNum, fromOnboarding]);

  const handleTimelineSpoilChange = useCallback(
    (value: boolean) => {
      setIsTimelineSpoil(value);
      if (!fromOnboarding) setNovelFeatureSpoil(bookIdNum || undefined, 'timeline', value);
    },
    [bookIdNum, fromOnboarding]
  );

  const handleCharacterAnalysisSpoilChange = useCallback(
    (value: boolean) => {
      setIsCharacterAnalysisSpoil(value);
      if (!fromOnboarding) setNovelFeatureSpoil(bookIdNum || undefined, 'character-analysis', value);
    },
    [bookIdNum, fromOnboarding]
  );

  const handleCharacterRelationshipsSpoilChange = useCallback(
    (value: boolean) => {
      setIsCharacterRelationshipsSpoil(value);
      if (!fromOnboarding) setNovelFeatureSpoil(
        bookIdNum || undefined,
        'character-relationships',
        value
      );
    },
    [bookIdNum, fromOnboarding]
  );
  const bannerRef = useRef<HTMLDivElement>(null);
  const pathContainerRef = useRef<HTMLDivElement>(null);
  const [hasActiveSubscription, setHasActiveSubscription] =
    useState<boolean>(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(true);

  // Library books for dropdown - use same hook as dashboard
  const { data: libraryData, isLoading: libraryLoading } =
    useUserLibraryForTab('in-progress');

  // Filter out current book from library - same structure as ContinueReading
  const libraryBooks = useMemo(() => {
    if (!libraryData?.items) return [];
    // Map items to extract book data, same as ContinueReading.tsx
    return libraryData.items
      .filter(item => item.book?.book_id !== bookIdNum)
      .map(item => ({
        book_id: item.book?.book_id,
        title: item.book?.title || 'بدون عنوان',
        author: item.book?.author || 'نویسنده نامشخص',
        cover_image_url: item.book?.cover_image_url ?? '',
        progress: Math.max(0, Math.min(100, Number(item.progress ?? 0))),
      }));
  }, [libraryData, bookIdNum]);

  // Debug logging and store learning path
  useEffect(() => {
    console.log('🎯 [LearningPath] Component mounted with:', {
      bookId,
      bookIdNum,
    });
    // Keep the active book available to review/reward pages.
    if (bookId) {
      localStorage.setItem('lastLearningPathBookId', bookId);
      if (fromOnboarding) {
        localStorage.setItem('onboarding_book_id', String(bookId));
      }
    }
  }, [bookId, bookIdNum, fromOnboarding]);

  const {
    data: book,
    isLoading: bookLoading,
    error: bookError,
  } = useBookDetail(bookIdNum, {
    enabled: !!bookIdNum,
  });

  // Fetch learning path (chapters/sections) for this book
  const {
    data: mySections,
    isLoading: learningLoading,
    error: learningError,
  } = useMySections(bookIdNum, {
    enabled: !!bookIdNum,
  });

  // Debug logging for learning data
  useEffect(() => {
    console.log('📚 [LearningPath] Learning state:', {
      mySections,
      isLoading: learningLoading,
      error: learningError,
    });
  }, [mySections, learningLoading, learningError]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        setSubscriptionLoading(true);
        const res = await paymentServiceApi.get<{
          is_active: boolean;
          message: string;
        }>('/subscriptions/status');
        setHasActiveSubscription(!!(res.success && res.data?.is_active));
      } catch (error) {
        console.error('Error checking subscription:', error);
        setHasActiveSubscription(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, []);

  const allSections = useMemo(
    () => (mySections?.chapters || []).flatMap(ch => ch.sections || []),
    [mySections?.chapters]
  );
  const hasNovelSection = useMemo(
    () => allSections.some(s => s.section_type === 'novel'),
    [allSections]
  );
  const hasNonNovelSection = useMemo(
    () => allSections.some(s => s.section_type !== 'novel'),
    [allSections]
  );
  const isNovelBook = hasNovelSection && !hasNonNovelSection;
  const learningPathGuideSteps = useMemo(
    () => [
      {
        id: 'learning-path-overview',
        title: 'مسیر مطالعه کتاب',
        description:
          'این مسیر مطالعه کتابه. هر دایره یه فصل از کتابه و هر فصل چند قسمت داره. با خوندن قسمت‌ها، به‌ترتیب فصل‌ها رو کامل می‌کنی تا در نهایت کل کتاب را بخونی.',
      },
      ...(isNovelBook
        ? [
            {
              id: 'learning-path-novel-fab',
              title: 'امکانات داستان',
              description:
                'اینجا می‌تونی با شخصیت‌ها حرف بزنی، تحلیل و روابطشون رو ببینی و خط زمانی داستان رو دنبال کنی.',
              target: '[data-guide="learning-path-novel-fab"]',
            },
          ]
        : []),
    ],
    [isNovelBook]
  );

  // Book-wide features require a subscription or a purchased book.
  const bookIsPremium = mySections?.is_premium ?? true; // Default to true if not specified
  const requiresSubscriptionForNovelFeatures =
    !subscriptionLoading && !hasActiveSubscription && !bookIsPremium;
  const canDisableNovelSpoil = fromOnboarding
    ? true
    : subscriptionLoading || learningLoading
      ? undefined
      : hasActiveSubscription || mySections?.is_premium === true;

  useEffect(() => {
    if (fromOnboarding || subscriptionLoading || learningLoading || canDisableNovelSpoil !== false) return;
    setIsTimelineSpoil(false);
    setIsCharacterAnalysisSpoil(false);
    setIsCharacterRelationshipsSpoil(false);
    setNovelFeatureSpoil(bookIdNum || undefined, 'timeline', false);
    setNovelFeatureSpoil(bookIdNum || undefined, 'character-analysis', false);
    setNovelFeatureSpoil(bookIdNum || undefined, 'character-relationships', false);
  }, [bookIdNum, canDisableNovelSpoil, fromOnboarding, learningLoading, subscriptionLoading]);
  const freeSectionId = useMemo(() => {
    const explicitlyFree = allSections.find(section => section.is_free);
    if (explicitlyFree) return explicitlyFree.section_id;
    const firstChapter = [...(mySections?.chapters ?? [])]
      .sort((a, b) => a.chapter_number - b.chapter_number)[0];
    return firstChapter?.sections?.find(section => section.section_number === 1)?.section_id;
  }, [allSections, mySections?.chapters]);

  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError,
    isFetched: timelineFetched,
    error: timelineErrorObj,
    refetch: refetchTimeline,
  } = useTimelineEvents(bookIdNum || 0, {
    enabled:
      !!bookIdNum && (isNovelBook || (fromOnboarding && hasNovelSection)),
    isSpoil: isTimelineSpoil,
  });

  const {
    data: characterAnalysisData,
    isLoading: characterAnalysisLoading,
    isError: characterAnalysisError,
    isFetched: characterAnalysisFetched,
    error: characterAnalysisErrorObj,
    refetch: refetchCharacterAnalysis,
  } = useCharacterAnalysis(bookIdNum || 0, {
    enabled:
      !!bookIdNum && (isNovelBook || (fromOnboarding && hasNovelSection)),
    isSpoil: isCharacterAnalysisSpoil,
  });

  const {
    data: characterRelationshipsData,
    isLoading: characterRelationshipsLoading,
    isError: characterRelationshipsError,
    isFetched: characterRelationshipsFetched,
    error: characterRelationshipsErrorObj,
    refetch: refetchCharacterRelationships,
  } = useCharacterRelationships(bookIdNum || 0, {
    enabled:
      !!bookIdNum && (isNovelBook || (fromOnboarding && hasNovelSection)),
    isSpoil: isCharacterRelationshipsSpoil,
  });

  const { data: bookCharactersData, isFetched: bookCharactersFetched } =
    useBookCharacters(bookIdNum || null, {
      enabled: !!bookIdNum && hasNovelSection && fromOnboarding,
    });

  const timelineSections = timelineData?.data?.sections ?? [];
  const analysisCharacters = characterAnalysisData?.data?.characters ?? [];
  const relationshipCharacters =
    characterRelationshipsData?.data?.characters ?? [];
  const onboardingFeatureDataReady =
    !fromOnboarding ||
    (timelineFetched &&
      characterAnalysisFetched &&
      characterRelationshipsFetched &&
      bookCharactersFetched);
  const onboardingFeatureFlags: OnboardingFeatureFlags | undefined =
    fromOnboarding && onboardingFeatureDataReady
      ? {
          hasChatCharacters: (bookCharactersData?.characters?.length ?? 0) > 0,
          hasCharacterInsights: analysisCharacters.some(character => {
            const relationshipCharacter = relationshipCharacters.find(
              item => item.character_name === character.character_name
            );
            return (
              (character.analyses?.length ?? 0) > 0 &&
              (relationshipCharacter?.relationships?.length ?? 0) > 0
            );
          }),
          // Locked timeline sections hide their events when spoiler protection is
          // enabled, but their presence still indicates timeline data exists.
          hasTimeline: timelineSections.length > 0,
        }
      : undefined;

  const mergedCharacters = useMemo(() => {
    const analysisList = characterAnalysisData?.data?.characters ?? [];
    const relationshipsList =
      characterRelationshipsData?.data?.characters ?? [];

    return analysisList.map(char => {
      const relationshipData = relationshipsList.find(
        r => r.character_name === char.character_name
      );
      return {
        ...char,
        relationships: relationshipData?.relationships ?? [],
        character_id: relationshipData?.character_id,
      };
    });
  }, [characterAnalysisData, characterRelationshipsData]);

  // Transform chapters to learning sessions with winding path positions
  const sessions = useMemo<LearningSession[]>(() => {
    const chapters = mySections?.chapters ?? [];
    if (!chapters.length) return [];

    const ordered = [...chapters].sort(
      (a, b) => a.chapter_number - b.chapter_number
    );

    // Calculate status based on progress
    const cps = ordered.map(ch =>
      Math.max(0, Math.min(100, Number(ch.chapter_progress ?? 0)))
    );
    const firstIncompleteIdx = cps.findIndex(p => p !== 100);

    return ordered.map((chapter, index) => {
      // Winding path logic
      const isRight = index % 2 === 0;
      const xPosition = isRight ? 65 : 35; // First node (index 0) on left (65%), second on right (35%)

      const cp = cps[index];

      const isCompleted = cp === 100;

      let isLocked = false;
      let isActive = false;

      if (firstIncompleteIdx !== -1) {
        isLocked = index > firstIncompleteIdx;
        isActive = index === firstIncompleteIdx;
      } else {
        // All completed
        isLocked = false;
        isActive = false;
      }

      // If it's the very first chapter and nothing started, it should be active
      if (firstIncompleteIdx === -1 && cps.every(p => p === 0)) {
        if (index === 0) isActive = true;
        if (index > 0) isLocked = true;
      }

      // Find the next section that is not done
      const nextSection = chapter.sections?.find(s => !s.is_done);
      const nextSectionTitle =
        nextSection?.title ||
        chapter.next_section_title ||
        chapter.sections?.[0]?.title ||
        chapter.title;

      return {
        id: chapter.chapter_id,
        title: chapter.title,
        description: `فصل ${chapter.chapter_number}`,
        isCompleted,
        isLocked,
        isActive,
        estimatedTime: 20,
        xpReward: 50,
        position: { x: xPosition, y: 0 },
        progress: cp,
        totalSections: chapter.sections?.length || 0,
        nextSectionId:
          nextSection?.section_id ?? chapter.next_section_id ?? undefined,
        nextSectionTitle: nextSectionTitle,
        displayTitle: nextSectionTitle,
        bookId: bookIdNum,
      };
    });
  }, [mySections, bookIdNum]);

  const analysisNodeId = -9999;
  const sessionsWithAnalysis = useMemo<LearningSession[]>(() => {
    if (fromOnboarding) return sessions;
    const analysisText = mySections?.analysis_text?.trim();
    if (!analysisText) return sessions;

    const analysisNode: LearningSession = {
      id: analysisNodeId,
      title: mySections?.title || 'تحلیل کتاب',
      description: 'تحلیل کتاب',
      kind: 'book-analysis',
      isCompleted: false,
      isLocked: false,
      isActive: true,
      estimatedTime: 5,
      xpReward: 0,
      position: { x: sessions.length % 2 === 0 ? 65 : 35, y: 0 },
      iconType: 'analysis',
      actionSubtitle: 'تحلیل کتاب',
      actionButtonText: 'شروع تحلیل',
      bookTitle: mySections?.title || 'تحلیل کتاب',
      bookId: bookIdNum,
    };

    return [...sessions, analysisNode];
  }, [
    fromOnboarding,
    mySections?.analysis_text,
    mySections?.title,
    sessions,
    bookIdNum,
  ]);

  // Open the current/last chapter automatically whenever a book path is entered.
  const defaultPathSession =
    sessions.find(session => session.isActive) ?? sessions[sessions.length - 1];
  useEffect(() => {
    if (!defaultPathSession) return;
    setSelectedSession(defaultPathSession);
    setIsModalOpen(true);
  }, [bookIdNum, defaultPathSession?.id]);

  // A completed section does not necessarily change the chapter id. Sync the
  // open modal with the refreshed chapter data so it shows the next section.
  useEffect(() => {
    if (!defaultPathSession) return;
    setSelectedSession(previous =>
      previous?.id === defaultPathSession.id ? defaultPathSession : previous
    );
  }, [defaultPathSession]);

  // Keep scroll restoration from the previous page from affecting this path.
  useEffect(() => {
    if (!defaultPathSession) return;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      const node = document.querySelector<HTMLElement>(
        `[data-learning-session-id="${defaultPathSession.id}"]`
      );
      node?.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'auto',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [bookIdNum, defaultPathSession?.id]);

  // Calculate SVG Path
  // We need to map the session positions to SVG coordinates
  const NODE_HEIGHT = 100; // Vertical distance between nodes (reduced for closer nodes)
  const CONTAINER_WIDTH = 100; // Use 100 for percentage-based width
  // Check if first node is completed to reduce top spacing
  const isFirstNodeCompleted =
    sessionsWithAnalysis.length > 0 && sessionsWithAnalysis[0].isCompleted;
  const START_Y = isFirstNodeCompleted ? 60 : 120; // Reduced padding when first node is completed
  const MAIN_PURPLE = bookTheme.primary;

  const { pathData, completedPathData } = useMemo(() => {
    if (sessionsWithAnalysis.length < 2)
      return { pathData: '', completedPathData: '' };

    let fullPath = '';
    let completedPath = '';

    sessionsWithAnalysis.forEach((session, index) => {
      const x = session.position.x; // Already in % (35 or 65)
      const y = START_Y + index * NODE_HEIGHT;

      if (index === 0) {
        fullPath += `M ${x} ${y}`;
      } else {
        const prevSession = sessionsWithAnalysis[index - 1];
        const prevX = prevSession.position.x;
        const prevY = START_Y + (index - 1) * NODE_HEIGHT;

        // Cubic bezier for smooth curve
        // Control points: halfway vertically, keeping x of start/end
        const cp1x = prevX;
        const cp1y = prevY + NODE_HEIGHT / 2;
        const cp2x = x;
        const cp2y = y - NODE_HEIGHT / 2;

        const segment = ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x} ${y}`;
        fullPath += segment;

        // If the previous session is completed, this segment should be colored
        if (prevSession.isCompleted) {
          completedPath += `M ${prevX} ${prevY} ${segment}`;
        }
      }
    });
    return { pathData: fullPath, completedPathData: completedPath };
  }, [sessionsWithAnalysis]);

  const handleSessionClick = (session: LearningSession) => {
    console.log('🎯 [LearningPath] Session clicked:', session);
    setSelectedSession(session);
    setIsModalOpen(true);

    // اگر در حالت onboarding هستیم، دیالوگ دانا را تغییر بده
    if (fromOnboarding) {
      window.dispatchEvent(
        new CustomEvent('onboarding-path-guide', {
          detail: { text: 'مطالعه رو شروع کن' },
        })
      );
    }
  };

  const logNovelFeatureClick = (
    feature: 'character_analysis' | 'timeline' | 'character_chat'
  ) => {
    void analyticsService.logEvent('novel_feature_click', {
      feature,
      page_context: 'learning_path',
      book_id: String(bookIdNum),
      book_title: mySections?.title || 'unknown',
    });
  };

  const handleStartChapter = (sessionId: number, sectionId?: number) => {
    console.log('🚀 [LearningPath] Starting chapter:', {
      sessionId,
      sectionId,
      bookId,
    });

    if (!bookId || !mySections) return;

    if (sessionId === analysisNodeId) {
      const chapters = mySections.chapters || [];
      const lastChapter = chapters[chapters.length - 1];
      const lastSection =
        lastChapter?.sections?.[lastChapter.sections.length - 1];
      const isNovelAnalysis = isNovelBook;
      const analysisState = {
        bookId: bookIdNum,
        chapterId: lastChapter?.chapter_id || sessionId,
        sectionId: lastSection?.section_id || sectionId || sessionId,
        sectionTitle: 'تحلیل کتاب',
        bookTitle: mySections.title,
      };

      if (isNovelAnalysis) {
        navigate('/novel-read', {
          state: {
            ...analysisState,
            isBookAnalysis: true,
            analysis_text: mySections.analysis_text,
            returnTo: '/learning/path',
            content: null,
          },
        });
      } else {
        navigate('/read', {
          state: {
            ...analysisState,
            bookTitle: mySections.title,
            isBookAnalysis: true,
            returnTo: '/learning/path',
            summary: mySections.analysis_text || '',
          },
        });
      }
      return;
    }

    // Find the section data from mySections
    const chapter = mySections.chapters.find(ch => ch.chapter_id === sessionId);
    const section = chapter?.sections?.find(s => s.section_id === sectionId);

    if (section) {
      if (
        fromOnboarding &&
        section.section_type === 'novel' &&
        !onboardingFeatureDataReady
      )
        return;
      const route =
        section.section_type === 'novel'
          ? fromOnboarding
            ? '/onboarding/novel-read'
            : '/novel-read'
          : fromOnboarding
            ? '/onboarding/read'
            : '/read';

      // Navigate to reading page with section data in state
      navigate(route, {
        state: {
          bookId: bookIdNum,
          bookTitle: mySections.title,
          chapterId: sessionId,
          sectionId: section.section_id,
          sectionTitle: section.title,
          summary: section.summary,
          previous_section_summary: section.previous_section_summary,
          trailer: section.trailer,
          content: section.content,
          source: state?.source,
          notificationCampaign: state?.notificationCampaign,
          onboardingPreview: fromOnboarding,
          onboardingFeatureFlags: fromOnboarding
            ? onboardingFeatureFlags
            : undefined,
        },
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSession(null);
  };

  const handleCloseDropdown = () => {
    if (disableBookSwitcher) return;
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        bannerRef.current &&
        !bannerRef.current.contains(event.target as Node)
      ) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle invalid bookId
  if (!bookId || bookIdNum === 0) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg"
        dir="rtl"
      >
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">
            شناسه کتاب نامعتبر است
          </p>
          <button
            onClick={() => navigate('/library')}
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian"
          >
            بازگشت به کتابخانه
          </button>
        </div>
      </div>
    );
  }

  // Loading state is handled inline now - no full page reload

  // Handle book error
  if (bookError || learningError) {
    console.error('❌ [LearningPath] Error:', bookError || learningError);
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg"
        dir="rtl"
      >
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="text-muted-foreground font-persian">
            خطا در بارگذاری اطلاعات
          </p>
          <p className="text-sm text-red-500 mt-2">
            {(bookError || learningError)?.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  // Handle no chapters (only show if not loading)
  if (
    !bookLoading &&
    !learningLoading &&
    (!sessionsWithAnalysis || sessionsWithAnalysis.length === 0)
  ) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg"
        dir="rtl"
      >
        <div className="text-center">
          <div className="text-4xl mb-4">📚</div>
          <p className="text-muted-foreground font-persian mb-2">
            هنوز فصلی برای این کتاب اضافه نشده است
          </p>
          <button
            onClick={() => navigate('/library')}
            className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian"
          >
            بازگشت به کتابخانه
          </button>
        </div>
      </div>
    );
  }

  // Check if we're in loading state
  const isLoading = bookLoading || learningLoading;

  // Helper function to convert numbers to Persian digits
  const toPersianDigits = (num: number | string): string => {
    return String(num).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  };

  // Calculate progress for the banner
  const totalProgress = Math.round(mySections?.book_progress || 0);
  const fabGradient = `linear-gradient(135deg, ${bookTheme.primary}, ${bookTheme.primaryDark})`;

  // Get the current active chapter for banner display
  const activeSession = sessionsWithAnalysis.find(s => s.isActive);
  const currentChapterTitle = activeSession?.title || 'مقدمه';

  return (
    <div
      className="min-h-screen bg-white dark:bg-dark-bg overflow-x-hidden hide-page-scrollbar"
      dir="rtl"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        paddingTop: fromOnboarding
          ? 'calc(var(--onboarding-header-h, 110px) + 10px)'
          : undefined,
      }}
    >
      <style>{`
        .hide-page-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Chapter Progress Card - Persian RTL - Sticky */}
      <div
        ref={bannerRef}
        className="sticky top-0 z-50 bg-white dark:bg-dark-bg w-full max-w-md mx-auto px-4 mb-2 pt-safe-top"
        dir="rtl"
      >
        {/* Top Bar with Back Button */}
        {!fromOnboarding && (
          <div className="flex items-center justify-start mb-1 px-2 mt-2">
            {/* Back Button - Right Side (RTL) */}
            <button
              onClick={() =>
                navigate(`/library/book`, {
                  state: { bookId, source: 'learning_path' },
                })
              }
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="بازگشت"
            >
              <svg
                className="w-5 h-5 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>
        )}

        <div className="relative h-20">
          {/* Main Container with Purple Gradient Background - Clickable */}
          <div
            onClick={() => {
              if (disableBookSwitcher) return;
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className="absolute inset-0 flex items-center justify-between pr-6 pl-0 py-3 shadow-lg cursor-pointer hover:opacity-95 transition-opacity"
            style={{
              borderRadius: '29px',
              backgroundImage: `linear-gradient(135deg, ${bookTheme.primary}, ${bookTheme.primaryDark})`,
            }}
          >
            {/* Halftone Dot Pattern Overlay - Right Side */}
            <div
              className="absolute right-0 top-0 h-full w-1/2 opacity-20 pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle, white 1.5px, transparent 2px)',
                backgroundSize: '8px 8px',
                borderRadius: '29px',
              }}
            />

            {/* Text Content - Right Side (RTL) */}
            <div className="relative z-10 text-white flex-1 flex flex-row items-center justify-between h-full pointer-events-none">
              <div className="flex flex-col justify-center">
                {isLoading ? (
                  <>
                    {/* Skeleton loading for title */}
                    <div className="h-6 w-32 bg-white/30 rounded-lg mb-2 animate-pulse"></div>
                    <div className="h-4 w-20 bg-white/20 rounded-lg animate-pulse"></div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold mb-1 leading-tight">
                      {book?.title || 'کتاب جاری'}
                    </h2>
                    <p className="text-sm font-medium opacity-90 leading-tight">
                      {currentChapterTitle}
                    </p>
                  </>
                )}
              </div>

              {/* Dropdown Arrow Indicator */}
              {!disableBookSwitcher && (
                <div className="p-2">
                  <svg
                    className={`w-5 h-5 text-white transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              )}
            </div>

            {/* Circular Progress Indicator - Left Side (RTL) */}
            <div className="relative z-20 flex-shrink-0 -ml-2">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg ring-4 ring-white/20">
                {isLoading ? (
                  /* Loading spinner */
                  <div
                    className="animate-spin rounded-full h-10 w-10 border-b-2"
                    style={{ borderBottomColor: bookTheme.primary }}
                  ></div>
                ) : (
                  <>
                    {/* SVG Circular Progress Bar */}
                    <svg
                      className="w-full h-full transform -rotate-90 p-1"
                      viewBox="0 0 100 100"
                    >
                      {/* Track Circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#f3f4f6"
                        strokeWidth="10"
                      />
                      {/* Progress Circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke={MAIN_PURPLE}
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 40}
                        strokeDashoffset={
                          2 * Math.PI * 40 * (1 - totalProgress / 100)
                        }
                        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                      />
                    </svg>
                    {/* Percentage Text */}
                    <span
                      className="absolute text-black font-bold text-sm"
                      dir="ltr"
                    >
                      {toPersianDigits(totalProgress)}٪
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Dropdown Menu - Seamless Extension of Banner */}
          {!disableBookSwitcher && (
            <div
              className={`absolute top-full left-0 right-0 z-30 transition-all duration-300 ease-out ${
                isDropdownOpen
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              {/* Speech Bubble Tail - Connects banner to dropdown */}
              <div className="flex justify-center">
                <div className="w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[12px] border-b-white"></div>
              </div>

              {/* Dropdown Content - White background */}
              <div className="overflow-hidden bg-white rounded-2xl shadow-xl border border-gray-100">
                {/* Horizontal Scrolling Container */}
                <div className="px-3 py-4">
                  <div
                    className="overflow-x-auto overflow-y-hidden"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    <style>{`
                    .hide-scrollbar::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                    <div
                      className="flex gap-4 hide-scrollbar justify-start pl-6"
                      dir="rtl"
                    >
                      {libraryLoading ? (
                        <div className="flex items-center justify-center w-full py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : libraryBooks.length === 0 ? (
                        <div className="flex items-center justify-center w-full py-4">
                          <p className="text-gray-500 text-sm font-persian">
                            کتاب دیگری در حال مطالعه نیست
                          </p>
                        </div>
                      ) : (
                        libraryBooks.map((book, index) => {
                          const progress = book.progress;
                          const itemTheme = getBookTheme(book.book_id);
                          // Rounded rectangle progress around book cover
                          const coverWidth = 72;
                          const coverHeight = 100;
                          const padding = 4;
                          const strokeWidth = 3;
                          const svgWidth =
                            coverWidth + padding * 2 + strokeWidth;
                          const svgHeight =
                            coverHeight + padding * 2 + strokeWidth;
                          const rectWidth = coverWidth + padding * 2;
                          const rectHeight = coverHeight + padding * 2;
                          const rx = 14; // border radius
                          // Perimeter of rounded rect (approximation)
                          const perimeter =
                            2 * (rectWidth + rectHeight - 4 * rx) +
                            2 * Math.PI * rx;
                          const progressLength = (progress / 100) * perimeter;

                          return (
                            <div
                              key={book.book_id || index}
                              className="flex-shrink-0"
                            >
                              <div className="flex flex-col items-center">
                                <div
                                  className="relative"
                                  style={{ width: svgWidth, height: svgHeight }}
                                >
                                  {/* Progress Border SVG - rounded rect around book */}
                                  <svg
                                    className="absolute inset-0"
                                    width={svgWidth}
                                    height={svgHeight}
                                    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                                  >
                                    {/* Background track */}
                                    <rect
                                      x={strokeWidth / 2}
                                      y={strokeWidth / 2}
                                      width={rectWidth}
                                      height={rectHeight}
                                      rx={rx}
                                      fill="none"
                                      stroke="#e5e7eb"
                                      strokeWidth={strokeWidth}
                                    />
                                    {/* Progress stroke - starts from top-center and goes clockwise */}
                                    <rect
                                      x={strokeWidth / 2}
                                      y={strokeWidth / 2}
                                      width={rectWidth}
                                      height={rectHeight}
                                      rx={rx}
                                      fill="none"
                                      stroke={itemTheme.primary}
                                      strokeWidth={strokeWidth}
                                      strokeLinecap="round"
                                      strokeDasharray={`${progressLength} ${perimeter}`}
                                      strokeDashoffset={-perimeter * 0.1}
                                      style={{
                                        transition:
                                          'stroke-dasharray 0.5s ease',
                                      }}
                                    />
                                  </svg>
                                  {/* Book Cover Button - centered inside */}
                                  <button
                                    onClick={() => {
                                      setIsDropdownOpen(false);
                                      navigate(`/learning/path`, {
                                        state: { bookId: book.book_id },
                                        replace: false,
                                      });
                                    }}
                                    className="absolute rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-200"
                                    style={{
                                      top: padding + strokeWidth / 2,
                                      left: padding + strokeWidth / 2,
                                      width: coverWidth,
                                      height: coverHeight,
                                      backgroundImage: `linear-gradient(145deg, ${itemTheme.primary}, ${itemTheme.primaryDark})`,
                                    }}
                                  >
                                    {book.cover_image_url ? (
                                      <img
                                        src={book.cover_image_url}
                                        alt={book.title}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div
                                        className="w-full h-full flex items-center justify-center"
                                        style={{
                                          backgroundImage: `linear-gradient(145deg, ${itemTheme.primary}, ${itemTheme.primaryDark})`,
                                        }}
                                      >
                                        <span className="text-white text-2xl">
                                          📚
                                        </span>
                                      </div>
                                    )}
                                  </button>
                                </div>
                                {/* Book title below cover - black text */}
                                <p className="text-gray-800 text-xs font-persian mt-1 text-center max-w-[80px] line-clamp-2 leading-tight font-medium">
                                  {book.title}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booka Max Banner - Show only if user doesn't have active subscription AND book is not premium */}
      {requiresSubscriptionForNovelFeatures && (
        <div className="w-full max-w-md mx-auto px-4 mb-4 mt-6">
          <HeroPromoBanner
            location="learning_path"
            buttonColor={bookTheme.primary}
            bookTitle={mySections?.title || book?.title}
          />
        </div>
      )}

      {/* Learning Path Container */}
      <div
        ref={pathContainerRef}
        className="relative w-full max-w-md mx-auto pb-32"
      >
        {/* Loading State - Inline */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div
              className="animate-spin rounded-full h-12 w-12 border-b-2 mb-4"
              style={{ borderBottomColor: bookTheme.primary }}
            ></div>
            <p className="text-muted-foreground font-persian text-sm">
              در حال بارگذاری...
            </p>
          </div>
        )}

        {/* Content - Only show when not loading */}
        {!isLoading && sessionsWithAnalysis.length > 0 && (
          <>
            {/* SVG Path Layer */}
            <svg
              className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
              viewBox={`0 0 ${CONTAINER_WIDTH} ${START_Y + sessionsWithAnalysis.length * NODE_HEIGHT}`}
              preserveAspectRatio="none"
              style={{
                height: `${START_Y + sessionsWithAnalysis.length * NODE_HEIGHT}px`,
              }}
            >
              {/* Background Path (Gray Dotted) */}
              <path
                d={pathData}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="8 8"
                vectorEffect="non-scaling-stroke"
              />
              {/* Completed Path (Book Theme Dotted) */}
              <path
                d={completedPathData}
                fill="none"
                stroke={bookTheme.primary}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="8 8"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {/* Nodes Layer */}
            <div className="relative z-10">
              {sessionsWithAnalysis.map((session, index) => {
                // We need to position these absolutely to match the SVG coordinates
                // OR use the same logic to place them.
                // Since we used % for x in calculation, we can use % here.
                const top = START_Y + index * NODE_HEIGHT - 48; // -48 to center the 96px node vertically on the point

                return (
                  <div
                    key={session.id}
                    className="absolute w-full flex justify-center"
                    style={{
                      top: `${top}px`,
                      left: 0,
                      pointerEvents: 'none', // Allow clicking through the row wrapper
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: `${session.position.x}%`,
                        marginLeft: '-48px', // Center the node (96px width / 2)
                        pointerEvents: 'auto',
                      }}
                    >
                      <div
                        className="relative flex flex-col items-center"
                        style={{ width: '96px' }}
                      >
                        <div
                          data-learning-session-id={session.id}
                          className="contents"
                        >
                          <LearningNode
                            session={session}
                            onClick={handleSessionClick}
                            isModalOpen={
                              isModalOpen && selectedSession?.id === session.id
                            }
                          />
                          {/* Render modal inline with node so it scrolls together */}
                          {isModalOpen &&
                            selectedSession?.id === session.id && (
                              <ChapterModal
                                isOpen={true}
                                onClose={handleCloseModal}
                                session={selectedSession}
                                onStartChapter={handleStartChapter}
                                position={
                                  index === sessionsWithAnalysis.length - 1
                                    ? 'above'
                                    : 'below'
                                }
                                isPremium={mySections?.is_premium ?? true}
                                canReadFreeSection={selectedSession.nextSectionId === freeSectionId}
                              />
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Spacer for bottom scrolling */}
            <div
              style={{
                height: `${START_Y + sessionsWithAnalysis.length * NODE_HEIGHT}px`,
              }}
            ></div>
          </>
        )}
      </div>

      {!fromOnboarding && isNovelBook && (
        <>
          {/* Backdrop overlay */}
          <AnimatePresence>
            {isFabOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[54]"
                onClick={() => setIsFabOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* FAB Container - Fixed position on the right */}
          <div
            className="fixed right-4 z-[55]"
            dir="rtl"
            style={{ bottom: 'calc(4.75rem + env(safe-area-inset-bottom))' }}
          >
            <div className="relative flex flex-col items-start gap-2">
              {/* Menu Items - Positioned above FAB */}
              <AnimatePresence>
                {isFabOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className="absolute bottom-[76px] right-2 flex flex-col items-start gap-2"
                  >
                    {/* Character Analysis Button */}
                    <motion.button
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 16 }}
                      transition={{ delay: 0.02 }}
                      onClick={() => {
                        logNovelFeatureClick('character_analysis');
                        setIsFabOpen(false);
                        setIsCharacterAnalysisOpen(true);
                      }}
                      className="flex flex-row items-center gap-2"
                    >
                      <span
                        className="w-9 h-9 rounded-full text-white shadow-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundImage: fabGradient }}
                      >
                        <Users className="w-4 h-4" />
                      </span>
                      <span
                        className="h-8 px-3 rounded-xl text-white shadow-lg flex items-center font-persian text-xs whitespace-nowrap"
                        style={{ backgroundImage: fabGradient }}
                      >
                        شخصیت ها
                      </span>
                    </motion.button>

                    {/* Timeline Button */}
                    <motion.button
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 16 }}
                      transition={{ delay: 0.06 }}
                      onClick={() => {
                        logNovelFeatureClick('timeline');
                        setIsFabOpen(false);
                        setIsTimelineOpen(true);
                      }}
                      className="flex flex-row items-center gap-2"
                    >
                      <span
                        className="w-9 h-9 rounded-full text-white shadow-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundImage: fabGradient }}
                      >
                        <History className="w-4 h-4" />
                      </span>
                      <span
                        className="h-8 px-3 rounded-xl text-white shadow-lg flex items-center font-persian text-xs whitespace-nowrap"
                        style={{ backgroundImage: fabGradient }}
                      >
                        خط زمانی داستان
                      </span>
                    </motion.button>

                    {/* Character Chat Button - No restriction */}
                    <motion.button
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 16 }}
                      transition={{ delay: 0.1 }}
                      onClick={() => {
                        logNovelFeatureClick('character_chat');
                        setIsFabOpen(false);
                        setChatOpen(true);
                      }}
                      className="flex flex-row items-center gap-2"
                    >
                      <span
                        className="w-9 h-9 rounded-full text-white shadow-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundImage: fabGradient }}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 10h8M8 14h5m-9 5l1.2-3.6a8 8 0 1114.6 0L21 19l-4-1.1A8 8 0 014 19z"
                          />
                        </svg>
                      </span>
                      <span
                        className="h-8 px-3 rounded-xl text-white shadow-lg flex items-center font-persian text-xs whitespace-nowrap"
                        style={{ backgroundImage: fabGradient }}
                      >
                        گفت و گو با شخصیت ها
                      </span>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Main FAB Button */}
              <button
                data-guide="learning-path-novel-fab"
                onClick={() => {
                  if (isModalOpen) handleCloseModal();
                  setIsFabOpen(prev => !prev);
                }}
                className="w-[60px] h-[60px] rounded-full text-white shadow-[0_6px_16px_rgba(30,24,100,0.28)] transition-all active:scale-95 flex items-center justify-center"
                style={{ backgroundImage: fabGradient }}
                aria-label="باز کردن امکانات رمان"
              >
                <Sparkles
                  className={`w-6 h-6 transition-transform ${isFabOpen ? 'rotate-12' : ''}`}
                />
              </button>
            </div>
          </div>

        </>
      )}

      <NovelCharacterChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        bookId={bookIdNum}
        analyticsBookTitle={book?.title || mySections?.title}
        bgColor="white"
        analyticsFlow={fromOnboarding ? 'onboarding' : 'normal'}
        canDisableSpoil={canDisableNovelSpoil}
      />

      <TimelineBottomSheet
        isOpen={isTimelineOpen}
        onClose={() => setIsTimelineOpen(false)}
        sections={timelineSections}
        isLoading={timelineLoading}
        isError={timelineError}
        errorMessage={timelineErrorObj?.message}
        onRetry={() => refetchTimeline()}
        theme="white"
        isSpoil={isTimelineSpoil}
        onSpoilChange={handleTimelineSpoilChange}
        canDisableSpoil={canDisableNovelSpoil}
        bookId={bookIdNum}
        bookTitle={book?.title || mySections?.title}
      />

      <CharacterAnalysisBottomSheet
        isOpen={isCharacterAnalysisOpen}
        onClose={() => setIsCharacterAnalysisOpen(false)}
        characters={mergedCharacters}
        isLoading={
          (!characterAnalysisData && characterAnalysisLoading) ||
          (!characterRelationshipsData && characterRelationshipsLoading)
        }
        isError={characterAnalysisError || characterRelationshipsError}
        errorMessage={
          characterAnalysisErrorObj?.message ||
          characterRelationshipsErrorObj?.message
        }
        onRetry={() => {
          refetchCharacterAnalysis();
          refetchCharacterRelationships();
        }}
        theme="white"
        isAnalysisSpoil={isCharacterAnalysisSpoil}
        onAnalysisSpoilChange={handleCharacterAnalysisSpoilChange}
        isRelationshipSpoil={isCharacterRelationshipsSpoil}
        onRelationshipSpoilChange={handleCharacterRelationshipsSpoilChange}
        canDisableSpoil={canDisableNovelSpoil}
        bookId={bookIdNum}
        bookTitle={book?.title || mySections?.title}
      />

      {/* Post-registration page guide. This is intentionally separate from onboarding. */}
      {!isOnboardingRoute &&
        user?.is_registered &&
        !user.is_guest &&
        bookIdNum > 0 && <FeatureGuideOverlay steps={learningPathGuideSteps} />}

      {showReferralInvite && !isOnboardingRoute && (
        <ReferralInviteModal onClose={() => setShowReferralInvite(false)} />
      )}

    </div>
  );
};

export default LearningPath;
